'use client';

import { PaginationState, Updater } from '@tanstack/react-table';
import { Pagination, PaginationMeta } from 'bff';
import { useMemo, useState } from 'react';
import { DataTablePaginationProps } from 'ui';

export interface PaginationMetaExtended extends PaginationMeta {
  page_size: number;
}

export const useStubTable = <T>(
  initMeta: PaginationMeta,
  initSize: number = 10,
) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initMeta.current_page - 1,
    pageSize: initSize,
  });

  const [paginationMeta, setPaginationMeta] =
    useState<PaginationMeta>(initMeta);

  const table = useMemo(
    () =>
      ({
        setPageSize: (updater: Updater<number>) =>
          setPagination((prev) => {
            const newPageSize =
              typeof updater === 'function' ? updater(prev.pageSize) : updater;
            return {
              ...prev,
              pageSize: newPageSize,
              pageIndex: 0,
            };
          }),
        setPageIndex: (updater: Updater<number>) =>
          setPagination((prev) => ({
            ...prev,
            pageIndex:
              typeof updater === 'function' ? updater(prev.pageIndex) : updater,
          })),
        previousPage: () =>
          setPagination((prev) => ({ ...prev, pageIndex: prev.pageIndex - 1 })),
        nextPage: () =>
          setPagination((prev) => ({ ...prev, pageIndex: prev.pageIndex + 1 })),
        getCanNextPage: () => !paginationMeta.is_last_page,
        getCanPreviousPage: () => !paginationMeta.is_first_page,
        getPageCount: () => paginationMeta.page_count,
        getState: () => ({
          pagination: {
            pageIndex: pagination.pageIndex,
            pageSize: pagination.pageSize,
          },
        }),
      } satisfies DataTablePaginationProps<T>['table']),
    [pagination, paginationMeta],
  );

  return {
    paginationMeta,
    setPaginationMeta,
    pagination,
    setPagination,
    table,
  };
};
